import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, Send, MapPin, Volume2, Square } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import { DISPATCH_SELECT } from "../hooks/useDispatches";
import { AppHeader } from "../components/AppHeader";
import { StatusPill } from "../components/StatusPill";
import { SendSheet } from "../components/SendSheet";
import { jobLink, relTime } from "../lib/share";
import { db } from "../lib/db";
import { useOutbox, toQueuedDispatch, retryItem } from "../lib/outbox";
import { CreditsSheet } from "../components/CreditsSheet";
import { DeleteJobButton } from "../components/DeleteJobButton";
import { PdfButton } from "../components/PdfButton";
import { StaticMap } from "../components/StaticMap";
import { speak, stopSpeaking, jobSpeech, readbackEnabled, speechSupported } from "../lib/speech";
import { ProofSection } from "../components/ProofSection";
import { ClaimSection } from "../components/ClaimSection";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : null);

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(undefined);
  const [sheet, setSheet] = useState(false);
  const [credits, setCredits] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const { items } = useOutbox();
  useEffect(() => () => stopSpeaking(), []);

  useEffect(() => {
    if (id.startsWith("outbox:")) {
      const key = id.slice(7);
      db.get("outbox", key).then((it) => setD(it ? toQueuedDispatch(it) : null));
      return;
    }
    db.get("dispatches", id).then((c) => c && setD((cur) => cur ?? c));
    supabase.from("dispatches").select(DISPATCH_SELECT).eq("id", id).maybeSingle().then(({ data, error }) => { if (!error) setD(data ?? null); else if (!/fetch|network/i.test(error.message)) setD(null); });
  }, [id, items]);

  if (d === undefined) return <main className="shell"><AppHeader back="/app" /><p className="text-muted" data-testid="job-loading">Loading…</p></main>;
  if (d === null) return <main className="shell"><AppHeader back="/app" /><p data-testid="job-not-found">Job not found.</p></main>;

  const timeline = [["Created", d.created_at], ["Sent", d.sent_at], ["Viewed", d.viewed_at],
    ...(d.claimed_at ? [[`Claimed by ${d.claimed_by_name}`, d.claimed_at, "claimed"]] : []),
    ...(d.claim_released_at ? [[d.claim_release_count > 1 ? `Released (${d.claim_release_count}×)` : "Released", d.claim_released_at, "released"]] : []),
    [d.completed_by_name ? `Completed by ${d.completed_by_name}` : "Completed", d.completed_at, "completed"]];

  const copy = async () => { await navigator.clipboard.writeText(jobLink(d.job_token)); toast.success("Link copied"); };

  return (
    <main className="shell has-fab" data-testid="job-detail-page">
      <AppHeader back="/app" />
      <section className="card fade-up">
        <div className="flex items-start justify-between gap-3">
          <h2 className="card-title" data-testid="job-detail-title">{d.title || d.task_summary}</h2>
          <StatusPill status={d.queued && d.sync_state === "failed" ? "failed" : d.status} />
        </div>
        {d.queued && d.sync_state === "failed" && (
          <div className="banner-danger mt-3 flex items-center gap-3" data-testid="needs-attention">
            <span className="flex-1 text-sm">{d.sync_error === "INSUFFICIENT_CREDITS" ? "Needs attention — out of credits" : d.sync_error === "WALLET_READ_ONLY" ? "Needs attention — account is read-only" : d.sync_error === "FAIR_USE_LIMIT" ? "Needs attention — fair-use limit reached this month" : `Needs attention — ${d.sync_error}`}</span>
            {d.sync_error === "INSUFFICIENT_CREDITS" || d.sync_error === "FAIR_USE_LIMIT" ? <button type="button" className="balance-pill" onClick={() => setCredits(true)} data-testid="fix-buy-credits">Buy credits</button>
              : <button type="button" className="balance-pill" onClick={() => retryItem(d.outbox.idempotency_key)} data-testid="fix-retry">Retry</button>}
          </div>
        )}
        {d.queued && d.sync_state !== "failed" && <p className="text-xs text-[#F6C945] mt-2" data-testid="queued-notice">Queued — will sync when you're back online.</p>}
        <p className="text-sm text-muted mt-1">{relTime(d.created_at)}{d.properties?.title && <> · <MapPin size={12} className="inline" /> {d.properties.title}</>}</p>
        <ul className="mt-4 space-y-1" data-testid="job-detail-tasks">{(d.tasks || []).map((t, i) => <li key={i} className="pill pill-blue">{t}</li>)}</ul>
        {d.hazards?.length > 0 && <div className="flex flex-wrap gap-2 mt-3" data-testid="job-detail-hazards">{d.hazards.map((h, i) => <span key={i} className="chip chip-yellow">⚠ {h}</span>)}</div>}
        {(d.access_code || d.access_notes) && <p className="text-sm text-muted mt-3" data-testid="job-detail-access">Access: {d.access_code ? "••••••••" : ""} {d.access_notes}</p>}
        {d.transcript && <p className="text-sm text-muted mt-3 result" data-testid="job-detail-transcript">“{d.transcript}”</p>}
        <p className="text-xs text-muted mt-3" data-testid="job-detail-recipient">To: <span className="notranslate" translate="no">{d.recipient_name ? `${d.recipient_name} · ` : ""}{d.recipient_phone || d.recipient_email || "—"}</span></p>
        {speechSupported() && readbackEnabled() && (
          <div className="flex gap-2 mt-4">
            {!speaking
              ? <button type="button" className="btn-secondary w-auto px-4 flex items-center gap-2" onClick={() => { const u = speak(jobSpeech(d), d.language); setSpeaking(!!u); const id = setInterval(() => { if (!window.speechSynthesis.speaking) { setSpeaking(false); clearInterval(id); } }, 400); }} data-testid="read-aloud-button"><Volume2 size={16} /> Read aloud</button>
              : <button type="button" className="btn-secondary w-auto px-4 flex items-center gap-2" onClick={() => { stopSpeaking(); setSpeaking(false); }} data-testid="read-aloud-stop"><Square size={14} fill="currentColor" /> Stop</button>}
          </div>
        )}
      </section>

      {d.latitude != null && (
        <section className="card mt-5 p-0 overflow-hidden fade-up" data-testid="job-detail-map">
          <div className="map-thumb"><StaticMap lat={d.latitude} lng={d.longitude} /></div>
          <p className="text-xs text-muted font-mono p-3" data-testid="job-detail-coords">{d.latitude}, {d.longitude}</p>
        </section>
      )}

      <ClaimSection dispatch={d} onChange={(n) => setD({ ...d, ...n })} />
      <ProofSection dispatch={d} />

      <section className="card mt-5 fade-up" data-testid="job-timeline">
        <p className="eyebrow mb-3">Timeline</p>
        {timeline.map(([label, at, key]) => (
          <div key={label} className={`tl-row ${at ? "done" : ""}`} data-testid={`timeline-${key || label.toLowerCase()}`}><span className="tl-dot" /><span className="flex-1 notranslate" translate="no">{label}</span><span className="text-xs text-muted text-right">{key === "completed" && at && d.completed_at_client && Math.abs(new Date(d.completed_at_client) - new Date(at)) > 60000 ? <span data-testid="timeline-completed-field">{fmt(d.completed_at_client)} <span className="block">(synced {fmt(at)})</span></span> : fmt(at) || "—"}</span></div>
        ))}
      </section>

      <div className="flex gap-3 mt-5">
        <button type="button" className="btn-secondary flex items-center justify-center gap-2" onClick={copy} data-testid="detail-copy-link"><Copy size={16} /> Copy link</button>
        {d.status !== "canceled" && <button type="button" className="btn-primary flex items-center justify-center gap-2" onClick={() => setSheet(true)} data-testid="detail-resend-button"><Send size={16} /> {d.status === "draft" ? "Send" : "Resend / share again"}</button>}
      </div>
      <div className="mt-3"><PdfButton dispatch={d} /></div>
      {d.report_path && d.report_expires_at && <p className="text-xs text-muted mt-2 text-center" data-testid="report-available-until">{d.task_summary === "[DATA EXPIRED & PURGED]" ? "Raw media purged · " : ""}Report available until {new Date(d.report_expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</p>}
      <div className="mt-3"><DeleteJobButton dispatch={d} onDeleted={() => navigate("/app")} /></div>
      {sheet && <SendSheet dispatch={d} onStatus={(n) => setD({ ...d, ...n })} onClose={() => setSheet(false)} onCancel={() => navigate("/app")} />}
      {credits && <CreditsSheet title="You're out of credits" subtitle="Top up and the queued job will sync automatically." onClose={() => { setCredits(false); if (d.outbox) retryItem(d.outbox.idempotency_key); }} />}
    </main>
  );
}
